import {useState} from 'react'
import {useMutation,useQuery,useQueryClient} from '@tanstack/react-query'
import {useTranslation} from 'react-i18next'
import {request} from '@/api/http'
import {Button} from '@/components/ui/button'
import type {Asset} from '@/types/nf'

const operators=['opening','conflict','satisfaction','reversal','hook','cinematic','shorten'] as const
export function ScriptOperators({chapterId}:{chapterId:string}) {
 const {t}=useTranslation()
 const client=useQueryClient()
 const [operator,setOperator]=useState<string>('opening')
 const [selected,setSelected]=useState<string[]>([])
 const key=['script-operators',chapterId]
 const query=useQuery({queryKey:key,queryFn:()=>request<Asset>(`/v1/chapters/${encodeURIComponent(chapterId)}/screenplay`)})
 const mutation=useMutation({mutationFn:()=>request<Asset>(`/v1/chapters/${encodeURIComponent(chapterId)}/screenplay/optimize`,{method:'POST',body:{asset_id:query.data?.id,operator,scene_ids:selected}}),onSuccess:async asset=>{
  client.setQueryData(key,asset)
  await Promise.all([
   client.invalidateQueries({queryKey:['chapter-assets',chapterId]}),
   client.invalidateQueries({queryKey:['chapter-screenplay',chapterId]}),
  ])
 }})
 let scenes:{scene_id:string;setting:string;action:string}[]=[]
 let warnings:string[]=[]
 try {const payload=JSON.parse(query.data?.payload ?? '{}');scenes=payload.screenplay?.scenes ?? [];warnings=payload.script_op?.warnings ?? []}catch{ /* API errors are shown below. */ }
 const error=query.error ?? mutation.error
 return <section className="space-y-3 border-t p-4">
  <h3 className="font-medium">{t('scriptOps.title')}</h3>
  <p className="text-sm text-muted-foreground">{t('scriptOps.scope')}</p>
  {scenes.map(scene=><label key={scene.scene_id} className="flex items-start gap-2 text-sm">
   <input type="checkbox" checked={selected.includes(scene.scene_id)} disabled={mutation.isPending} onChange={e=>setSelected(e.target.checked?[...selected,scene.scene_id]:selected.filter(id=>id!==scene.scene_id))}/>
   <span>{scene.setting} — {scene.action}</span>
  </label>)}
  <label className="flex items-center gap-2 text-sm"><span>{t('scriptOps.operator')}</span>
   <select className="rounded-md border bg-background p-2" value={operator} disabled={mutation.isPending} onChange={e=>setOperator(e.target.value)}>
    {operators.map(op=><option key={op} value={op}>{t(`scriptOps.${op}`)}</option>)}
   </select>
  </label>
  <Button disabled={!scenes.length || mutation.isPending} onClick={()=>mutation.mutate()}>{t(mutation.isPending?'scriptOps.running':'scriptOps.apply')}</Button>
  {error && <p role="alert" className="text-sm text-destructive">{error.message}</p>}
  {mutation.isSuccess && <p role="status" className="text-sm">{t('scriptOps.saved')}</p>}
  {warnings.includes('length_drift') && <p role="status" className="text-sm text-muted-foreground">{t('scriptOps.lengthDrift')}</p>}
 </section>
}
